import { useCallback, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useForm, FormProvider, useFormContext, useWatch } from 'react-hook-form';
import type { FieldErrors, FieldValues } from 'react-hook-form';
import {
  Alert,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
  Title,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useMutation } from '@tanstack/react-query';
import { IconAlertOctagonFilled } from '@tabler/icons-react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import type { SiteConfig, SectionSchema, FieldSchema } from '../types/schema';
import FormField from './FormField';
import { requestDraftRestore, useFormDraft } from '../hooks/useFormDraft';
import { renderMarkdownLabel } from '../utils/markdownLabel';
import ProgramFooter from './ProgramFooter';
import ProgramHeader from './ProgramHeader';
import ProgramSupportInfo from './ProgramSupportInfo';
import SessionUtilityBar from './SessionUtilityBar';
import { submitEnrollmentProductForm, submitForm, validateEligibility, validateEnrollmentProductEligibility, SubmissionError } from '../api/submit';
import { uploadAttachment } from '../api/attachments';
import { saveDraft, fetchDraft } from '../api/result';
import { ApiError } from '../api/http';
import { clearSessionExpiry, logoutSession } from '../api/auth';
import { resolveProgramIdentity } from '../hooks/useResolvedProgram';
import { isDevelopmentEnvironment } from '../utils/environment';
import { buildReturnToPath } from '../utils/portalPath';
import { buildSampleValuesForPage } from '../utils/sampleData';
import { evaluateShowIf } from '../utils/showIf';
import { buildSubmissionResultPath } from '../utils/enrollmentProduct';

interface GenericFormProps {
  config: SiteConfig;
  sessionEmail?: string;
  product?: string;
}

interface SubmitAlertState {
  title: string;
  message: string;
  details?: string[];
}

type SubmissionPhase = 'idle' | 'submitting' | 'uploading';

interface FormSectionProps {
  section: SectionSchema;
  isMobile: boolean;
  onFilesSelected?: (fieldId: string, files: File[]) => File[];
}

interface FormFieldContainerProps {
  field: FieldSchema;
  columns?: number;
  onFilesSelected?: (fieldId: string, files: File[]) => File[];
}

function getFirstErrorFieldPath(errors: FieldErrors<FieldValues>, prefix = ''): string | null {
  for (const [key, value] of Object.entries(errors)) {
    if (!value) {
      continue;
    }

    const path = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'object' && 'message' in value && value.message) {
      return path;
    }

    if (typeof value === 'object') {
      const nestedPath = getFirstErrorFieldPath(value as FieldErrors<FieldValues>, path);
      if (nestedPath) {
        return nestedPath;
      }
    }
  }

  return null;
}

function FormFieldContainer({ field, columns, onFilesSelected }: FormFieldContainerProps) {
  const { control, getValues, setValue, unregister } = useFormContext<Record<string, unknown>>();
  const allValues = useWatch({ control });
  const isVisible = evaluateShowIf(field.showIf, allValues);
  const previousIsVisibleRef = useRef<boolean | null>(null);
  const cachedValueRef = useRef<{ hasValue: boolean; value: unknown }>({ hasValue: false, value: undefined });

  useEffect(() => {
    const previousIsVisible = previousIsVisibleRef.current;
    if (previousIsVisible === isVisible) {
      return;
    }

    if (!isVisible) {
      if (field.type === 'file') {
        onFilesSelected?.(field.id, []);
      } else if (previousIsVisible === true) {
        cachedValueRef.current = { hasValue: true, value: getValues(field.id) };
      }
      unregister(field.id);
    } else if (previousIsVisible === false && cachedValueRef.current.hasValue) {
      setValue(field.id, cachedValueRef.current.value, { shouldDirty: true });
    }
    previousIsVisibleRef.current = isVisible;
  }, [field.id, field.type, getValues, isVisible, onFilesSelected, setValue, unregister]);

  if (!isVisible) {
    return null;
  }

  const requestedSpan = field.span ?? 1;
  const effectiveSpan = columns == null
    ? 1
    : Math.min(Math.max(requestedSpan, 1), columns);

  return (
    <div
      data-field-id={field.id}
      style={effectiveSpan > 1 ? { gridColumn: `span ${effectiveSpan}` } : undefined}
    >
      <FormField field={field} onFilesSelected={onFilesSelected} />
    </div>
  );
}

function FormSection({ section, isMobile, onFilesSelected }: FormSectionProps) {
  const { control, unregister } = useFormContext<Record<string, unknown>>();
  const allValues = useWatch({ control });
  const isVisible = evaluateShowIf(section.showIf, allValues);
  const previousIsVisibleRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (previousIsVisibleRef.current === isVisible) {
      return;
    }

    if (!isVisible) {
      for (const field of section.fields) {
        if (field.type === 'file') {
          onFilesSelected?.(field.id, []);
        }
        unregister(field.id);
      }
    }
    previousIsVisibleRef.current = isVisible;
  }, [isVisible, onFilesSelected, section.fields, unregister]);

  if (!isVisible) {
    return null;
  }

  const configuredColumns = section.columns ?? 1;
  const effectiveColumns = isMobile ? 1 : configuredColumns;

  return (
    <Paper p="lg" withBorder radius="md" className="portal-panel">
      <Title
        order={4}
        mb={section.description ? 'xs' : 'md'}
        className="portal-section-title"
      >
        {section.title}
      </Title>
      {section.description && (
        <Text size="sm" c="dimmed" mb="md">
          {renderMarkdownLabel(section.description)}
        </Text>
      )}
      {effectiveColumns > 1 ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${effectiveColumns}, 1fr)`,
            gap: 'var(--mantine-spacing-sm)',
          }}
        >
          {section.fields.map((field) => (
            <FormFieldContainer
              key={field.id}
              field={field}
              columns={effectiveColumns}
              onFilesSelected={onFilesSelected}
            />
          ))}
        </div>
      ) : (
        <Stack gap="sm">
          {section.fields.map((field) => (
            <FormFieldContainer key={field.id} field={field} onFilesSelected={onFilesSelected} />
          ))}
        </Stack>
      )}
    </Paper>
  );
}

function buildDefaultValues(config: SiteConfig, sessionEmail?: string): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};

  for (const page of config.pages.form) {
    for (const section of page.sections) {
      for (const field of section.fields) {
        if (field.defaultValue !== undefined) {
          let value = field.defaultValue;
          if (typeof value === 'string' && value === '{{currentUser.email}}' && sessionEmail) {
            value = sessionEmail;
          }
          defaults[field.id] = value;
        }
      }
    }
  }

  return defaults;
}

function getDraftIgnoredFieldIds(config: SiteConfig): string[] {
  const ignored: string[] = [];

  for (const page of config.pages.form) {
    for (const section of page.sections) {
      for (const field of section.fields) {
        if (field.type === 'hidden' || field.type === 'file' || field.sensitive != null) {
          ignored.push(field.id);
        }
      }
    }
  }

  return ignored;
}

function stripFileValues(config: SiteConfig, values: Record<string, unknown>): Record<string, unknown> {
  const fileFieldIds = new Set<string>();
  for (const page of config.pages.form) {
    for (const section of page.sections) {
      for (const field of section.fields) {
        if (field.type === 'file') {
          fileFieldIds.add(field.id);
        }
      }
    }
  }
  const sanitized = { ...values };
  for (const fieldId of fileFieldIds) {
    delete sanitized[fieldId];
  }
  return sanitized;
}

function stripDraftValues(config: SiteConfig, values: Record<string, unknown>): Record<string, unknown> {
  const sanitized = stripFileValues(config, values);
  for (const page of config.pages.form) {
    for (const section of page.sections) {
      for (const field of section.fields) {
        if (field.sensitive != null) {
          delete sanitized[field.id];
        }
      }
    }
  }
  return sanitized;
}

/** Recursively trim all string values in a form values object. */
function trimStringValues(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      result[key] = value.trim();
    } else if (value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof File)) {
      result[key] = trimStringValues(value as Record<string, unknown>);
    } else {
      result[key] = value;
    }
  }
  return result;
}

export default function GenericForm({ config, sessionEmail, product }: GenericFormProps) {
  const { draftScopeKeys, sessionScopeKey } = resolveProgramIdentity(config);
  const draftScopeKey = product ? `${draftScopeKeys[config.siteType]}:${product}` : draftScopeKeys[config.siteType];
  const methods = useForm<Record<string, unknown>>({
    mode: 'onChange',
    defaultValues: buildDefaultValues(config, sessionEmail),
  });
  const navigationType = useNavigationType();
  const location = useLocation();
  const navigate = useNavigate();
  const restoreDraft = Boolean((location.state as { restoreDraft?: boolean } | null)?.restoreDraft);
  const resumeDraftId = (location.state as { resumeDraftId?: string } | null)?.resumeDraftId ?? null;
  const [submitAlert, setSubmitAlert] = useState<SubmitAlertState | null>(null);
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(resumeDraftId);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [submissionPhase, setSubmissionPhase] = useState<SubmissionPhase>('idle');
  const [submissionAttachmentCount, setSubmissionAttachmentCount] = useState(0);
  const selectedFilesRef = useRef(new Map<string, File[]>());
  const idempotencyKeysRef = useRef(new WeakMap<File, string>());
  const resumeLoadedRef = useRef(false);
  const submitAlertRef = useRef<HTMLDivElement>(null);
  const handleFilesSelected = useCallback((fieldId: string, files: File[]) => {
    const otherFiles = Array.from(selectedFilesRef.current.entries())
      .filter(([existingFieldId]) => existingFieldId !== fieldId)
      .flatMap(([, existingFiles]) => existingFiles);
    const acceptedFiles = files.slice(0, Math.max(0, 3 - otherFiles.length));
    if (acceptedFiles.length > 0) {
      selectedFilesRef.current.set(fieldId, acceptedFiles);
    } else {
      selectedFilesRef.current.delete(fieldId);
    }
    return acceptedFiles;
  }, []);

  useEffect(() => {
    if (submitAlert) {
      submitAlertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [submitAlert]);

  // Multi-page form: currentPageIndex tracked in location state so browser back/forward navigates between pages.
  const currentPageIndex = (location.state as { wizardPage?: number } | null)?.wizardPage ?? 0;
  const currentFormPage = config.pages.form[currentPageIndex] ?? config.pages.form[0];
  const previousPageIndex = currentPageIndex > 0 ? currentPageIndex - 1 : null;
  const nextPageIndex = currentPageIndex + 1 < config.pages.form.length ? currentPageIndex + 1 : null;
  const visibleSections = currentFormPage?.sections ?? [];
  const submitLabel = currentFormPage?.submitLabel ?? (config.siteType === 'escalations' ? 'Submit Escalation' : 'Submit Enrollment');

  useEffect(() => {
    if (!restoreDraft) {
      return;
    }

    navigate(location.pathname, { replace: true });
  }, [location.pathname, navigate, restoreDraft]);

  // Load form values from a server-stored draft when resuming from the landing page.
  useEffect(() => {
    if (!resumeDraftId || resumeLoadedRef.current) {
      return;
    }
    resumeLoadedRef.current = true;

    void fetchDraft(config.siteType, resumeDraftId).then((draft) => {
      methods.reset(draft.values);
    }).catch(() => {
      // If the draft can't be loaded, start fresh — don't block the user.
    });
  // Only run once on mount for the given resumeDraftId.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeDraftId]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [currentPageIndex]);

  const { clearDraft, saveDraftSnapshot } = useFormDraft({
    control: methods.control,
    reset: methods.reset,
    scopeKey: draftScopeKey,
    navigationType,
    allowRestore: restoreDraft,
    ignoredFieldIds: getDraftIgnoredFieldIds(config),
  });

  const submitMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => product
      ? submitEnrollmentProductForm(product, values, currentDraftId ?? undefined)
      : submitForm(config.siteType, values, currentDraftId ?? undefined),
  });

  const eligibilityMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => product
      ? validateEnrollmentProductEligibility(product, values)
      : validateEligibility(config.siteType, values),
  });

  const logoutMutation = useMutation({
    mutationFn: logoutSession,
  });

  const isPending = submitMutation.isPending || eligibilityMutation.isPending || submissionPhase !== 'idle';
  const activeSubmitLabel = submissionPhase === 'submitting'
    ? 'Submitting escalation...'
    : submissionPhase === 'uploading'
      ? `Uploading ${submissionAttachmentCount} attachment${submissionAttachmentCount === 1 ? '' : 's'}...`
      : submitLabel;
  const showSampleData = isDevelopmentEnvironment();
  const isLovable = config.theme.uiStyle === 'lovable';
  const isMobile = useMediaQuery('(max-width: 48em)');
  const canSaveDraft = config.siteType === 'escalations' && config.solution === 'escalationiq';
  const sensitiveDraftLabels = config.pages.form.flatMap((page) =>
    page.sections.flatMap((section) => section.fields
      .filter((field) => field.sensitive != null)
      .map((field) => field.label)
      .filter((label): label is string => Boolean(label))),
  );

  const handleFillSampleData = () => {
    methods.reset({
      ...methods.getValues(),
      ...buildSampleValuesForPage(currentFormPage),
    });
    methods.clearErrors();
  };

  const handlePreviousPage = () => {
    if (previousPageIndex == null) {
      return;
    }

    navigate('.', { state: { wizardPage: previousPageIndex } });
  };

  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    try {
      const values = stripDraftValues(config, methods.getValues());
      const response = await saveDraft(config.siteType, values, currentDraftId ?? undefined);
      setCurrentDraftId(response.draftId);
      notifications.show({
        message: sensitiveDraftLabels.length > 0
          ? `Draft saved without ${sensitiveDraftLabels.join(', ')}. Re-enter ${sensitiveDraftLabels.length === 1 ? 'it' : 'them'} when you resume.`
          : 'Draft saved. You can return to this form later from the home screen.',
        color: 'green',
        autoClose: 4000,
      });
    } catch {
      notifications.show({
        message: 'Unable to save draft. Please try again.',
        color: 'red',
        autoClose: 4000,
      });
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleLogout = async () => {
    setSubmitAlert(null);

    try {
      await logoutMutation.mutateAsync();
      clearSessionExpiry(sessionScopeKey);
      navigate('/auth/request', { replace: true });
    } catch (error) {
      setSubmitAlert({
        title: 'Unable to log out',
        message: error instanceof ApiError
          ? error.message
          : 'An error occurred while logging out. Please try again.',
      });
    }
  };

  const navigateToResultWithDraftRestore = (resultId: string, values: Record<string, unknown>) => {
    saveDraftSnapshot(stripFileValues(config, values));
    requestDraftRestore(draftScopeKey, 'pop-only');
    navigate(buildSubmissionResultPath(config.siteType, resultId, product));
  };

  const getIdempotencyKey = (file: File) => {
    const existing = idempotencyKeysRef.current.get(file);
    if (existing) return existing;
    const key = typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    idempotencyKeysRef.current.set(file, key);
    return key;
  };

  const uploadSelectedFiles = async (resultId: string) => {
    const uploads = Array.from(selectedFilesRef.current.entries()).flatMap(([fieldId, files]) =>
      files.map((file) => uploadAttachment(
        config.siteType,
        resultId,
        fieldId,
        file,
        getIdempotencyKey(file),
      )),
    );
    await Promise.all(uploads);
  };

  const onSubmit = async (rawValues: Record<string, unknown>) => {
    // Trim all string values to prevent accidental whitespace in submissions.
    const values = trimStringValues(rawValues);
    methods.clearErrors();
    setSubmitAlert(null);

    const action = currentFormPage?.action ?? 'submit';

    if (action === 'validateEligibility') {
      try {
        const result = await eligibilityMutation.mutateAsync(values);
        if (!result.eligible) {
          if (!result.resultId) {
            setSubmitAlert({
              title: 'Unable to show result',
              message: 'Eligibility validation returned an ineligible result without a result ID.',
            });
            return;
          }

          navigateToResultWithDraftRestore(result.resultId, values);
          return;
        }
        // Eligible: advance to the next page. Pushing a history entry so browser back returns here.
        if (nextPageIndex != null) {
          saveDraftSnapshot(values);
          navigate('.', { state: { wizardPage: nextPageIndex } });
        }
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          clearSessionExpiry(sessionScopeKey);
          navigate('/auth/request', { replace: true, state: { returnTo: buildReturnToPath(location) } });
          return;
        }

        if (error instanceof SubmissionError && error.resultId) {
          navigateToResultWithDraftRestore(error.resultId, values);
          return;
        }

        setSubmitAlert({
          title: 'Unable to check eligibility',
          message: error instanceof SubmissionError
            ? error.message
            : 'An error occurred while checking eligibility. Please try again.',
        });
      }
      return;
    }

    // action === 'submit' or flat mode — existing enrollment submission path
    const isEscalationSubmission = config.siteType === 'escalations';
    const attachmentCount = Array.from(selectedFilesRef.current.values())
      .reduce((count, files) => count + files.length, 0);
    const progressNotificationId = isEscalationSubmission
      ? notifications.show({
          loading: true,
          title: 'Submitting escalation',
          message: 'Sending the escalation details.',
          autoClose: false,
          withCloseButton: false,
        })
      : null;
    if (isEscalationSubmission) {
      setSubmissionAttachmentCount(attachmentCount);
      setSubmissionPhase('submitting');
    }

    try {
      const sanitizedValues = stripFileValues(config, values);
      const result = await submitMutation.mutateAsync(sanitizedValues);
      let attachmentUploadFailed = false;
      if (progressNotificationId && attachmentCount > 0) {
        setSubmissionPhase('uploading');
        notifications.update({
          id: progressNotificationId,
          loading: true,
          title: 'Uploading attachments',
          message: `Uploading ${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}.`,
          autoClose: false,
          withCloseButton: false,
        });
        try {
          await uploadSelectedFiles(result.result_id);
        } catch {
          attachmentUploadFailed = true;
          notifications.update({
            id: progressNotificationId,
            loading: false,
            color: 'yellow',
            title: 'Escalation submitted',
            message: 'One or more attachments were not uploaded. You can retry them from the escalation status page.',
            autoClose: 6000,
            withCloseButton: true,
          });
        }
      }
      if (progressNotificationId && !attachmentUploadFailed) {
        notifications.update({
          id: progressNotificationId,
          loading: false,
          color: 'green',
          title: 'Escalation submitted',
          message: attachmentCount > 0
            ? `The escalation and ${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'} were submitted.`
            : 'The escalation was submitted.',
          autoClose: 4000,
          withCloseButton: true,
        });
      }
      clearDraft();
      setCurrentDraftId(null);
      navigate(buildSubmissionResultPath(config.siteType, result.result_id, product));
    } catch (error) {
      saveDraftSnapshot(values);

      if (progressNotificationId) {
        notifications.update({
          id: progressNotificationId,
          loading: false,
          color: 'red',
          title: 'Escalation not submitted',
          message: 'The escalation could not be submitted. Review the form and try again.',
          autoClose: 6000,
          withCloseButton: true,
        });
      }

      if (error instanceof ApiError && error.status === 401) {
        if (progressNotificationId) {
          notifications.hide(progressNotificationId);
        }
        clearSessionExpiry(sessionScopeKey);
        navigate('/auth/request', { replace: true, state: { returnTo: buildReturnToPath(location) } });
        return;
      }
      const message = error instanceof SubmissionError
        ? error.message
        : `The ${config.siteType === 'escalations' ? 'escalation' : 'enrollment'} could not be submitted. You can return to the form and try again.`;

      if (error instanceof SubmissionError && error.fieldErrors && Object.keys(error.fieldErrors).length > 0) {
        Object.entries(error.fieldErrors).forEach(([field, msg]) => {
          methods.setError(field, { type: 'server', message: msg as string });
        });
        setSubmitAlert({ title: 'Please correct the highlighted fields', message });
        return;
      }

      if (!(error instanceof SubmissionError) || !error.resultId) {
        setSubmitAlert({
          title: 'Unable to show result',
          message,
        });
        return;
      }

      navigateToResultWithDraftRestore(error.resultId, values);
    } finally {
      if (isEscalationSubmission) {
        setSubmissionPhase('idle');
        setSubmissionAttachmentCount(0);
      }
    }
  };

  const onInvalidSubmit = (errors: FieldErrors<Record<string, unknown>>) => {
    setSubmitAlert({
      title: 'Please complete all required fields',
      message: 'The form cannot be submitted until every field marked with * has been completed. Missing fields are highlighted below.',
    });

    const firstErrorPath = getFirstErrorFieldPath(errors);
    if (!firstErrorPath) {
      return;
    }

    const topLevelFieldId = firstErrorPath.split('.')[0];
    const fieldContainer = document.querySelector<HTMLElement>(`[data-field-id="${topLevelFieldId}"]`);
    fieldContainer?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <FormProvider {...methods}>
      <Container
        size="sm"
        pt="xl"
        pb={0}
        className={config.theme.contentSurface ? 'portal-page portal-page--content-surface' : 'portal-page'}
      >
        <Stack gap={config.theme.contentSurface ? 0 : 'lg'} style={{ flex: 1 }}>
          <ProgramHeader
            logoUrl={config.theme.logoUrl}
            programName={config.pages.title ?? config.theme.displayName}
            portalName={config.theme.portalName}
            titleAlign={config.theme.titleAlign}
            containerSize="sm"
            mb={0}
            showTopBar={isLovable}
            showTopBarLogo={config.theme.showTopBarLogo !== false}
            showTopBarPortalName={config.theme.showTopBarPortalName !== false}
            topBarLogoUrl={config.theme.topBarLogoUrl}
            topBarLogoAlt={config.theme.topBarLogoAlt}
            topBarLogoWidth={config.theme.topBarLogoWidth}
            topBarLogoHeight={config.theme.topBarLogoHeight}
            sessionEmail={isLovable ? sessionEmail : undefined}
            onLogout={isLovable ? () => { void handleLogout(); } : undefined}
            isLoggingOut={isLovable ? logoutMutation.isPending : false}
          />

          {!isLovable && (
            <SessionUtilityBar
              sessionEmail={sessionEmail}
              showHome
              onGoHome={() => navigate('/')}
              onLogout={() => { void handleLogout(); }}
              isLoggingOut={logoutMutation.isPending}
            />
          )}

          <div className={config.theme.contentSurface ? 'portal-content-surface portal-form-content-surface' : 'portal-form-content'}>
          <Group justify="space-between" align="center">
            <Text size="xs" c="dimmed">
              <Text component="span" c="red">*</Text> indicates required fields
            </Text>
            {showSampleData && (
              <Button variant="subtle" size="compact-xs" p={0} onClick={handleFillSampleData}>
                Fill Sample Data
              </Button>
            )}
          </Group>

          <form onSubmit={methods.handleSubmit(onSubmit, onInvalidSubmit)} noValidate>
            <Stack gap="lg">
              <div ref={submitAlertRef}>
              {submitAlert && (
                <Alert
                  color="red"
                  icon={submitAlert.title === 'Please complete all required fields' ? <IconAlertOctagonFilled /> : undefined}
                  title={submitAlert.title === 'Please complete all required fields'
                    ? <Text c="#80170c" fw={700}>{submitAlert.title}</Text>
                    : submitAlert.title}
                  variant="light"
                >
                  <Stack gap="xs">
                    <Text c={submitAlert.title === 'Please complete all required fields' ? '#80170c' : undefined}>
                      {submitAlert.message}
                    </Text>
                    {submitAlert.details?.map((detail: string) => (
                      <Text key={detail} size="sm">{detail}</Text>
                    ))}
                  </Stack>
                </Alert>
              )}
              </div>

              {visibleSections.map((section) => (
                <FormSection
                  key={section.id}
                  section={section}
                  isMobile={isMobile}
                  onFilesSelected={handleFilesSelected}
                />
              ))}

              <Group justify="flex-end" wrap="wrap">
                  {previousPageIndex != null && (
                    <Button
                      type="button"
                      size="md"
                      variant="default"
                      onClick={handlePreviousPage}
                      disabled={isPending}
                    >
                      Back
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="md"
                    variant="default"
                    onClick={() => navigate('/')}
                    disabled={isPending || isSavingDraft}
                  >
                    Cancel
                  </Button>
                  {canSaveDraft && (
                    <Tooltip
                      label={`Sensitive fields (${sensitiveDraftLabels.join(', ')}) are not saved and must be re-entered.`}
                      disabled={sensitiveDraftLabels.length === 0}
                      withArrow
                    >
                      <Button
                        type="button"
                        size="md"
                        variant="default"
                        onClick={() => { void handleSaveDraft(); }}
                        loading={isSavingDraft}
                        disabled={isPending}
                      >
                        Save Draft
                      </Button>
                    </Tooltip>
                  )}
                  <Button
                    type="submit"
                    size="md"
                    loading={isPending}
                    className="portal-primary-button"
                  >
                    {activeSubmitLabel}
                  </Button>
              </Group>
            </Stack>
          </form>

          <ProgramSupportInfo support={currentFormPage?.showSupportInfo === true ? config.support : undefined} />
          </div>

          {isLovable && (
            <ProgramFooter
              visible
              tagline={config.theme.footerTagline}
              legalText={config.theme.footerLegalText}
              prescribingInformationUrl={config.theme.prescribingInformationUrl}
              feedbackUrl={config.theme.feedbackUrl}
              showLogo={config.theme.showFooterLogo !== false}
              logoUrl={config.theme.footerLogoUrl}
            />
          )}
        </Stack>
      </Container>
    </FormProvider>
  );
}
